import type { LabRouteEditorModel, ProviderRow } from '.';
import {
    discard as discardAction,
    draftFromRoute,
    draftsEqual,
    draftToUpdateRequest,
    failSave as failSaveAction,
    finishSave as finishSaveAction,
    hasIssues,
    initialLabRouteEditorState,
    labRouteEditorReducer,
    NO_ISSUES,
    setBidPriority as setBidPriorityAction,
    setCanManage as setCanManageAction,
    setField as setFieldAction,
    setProviderSearch as setProviderSearchAction,
    setScope as setScopeAction,
    startSave as startSaveAction,
    syncRoute as syncRouteAction,
    targetProviders as targetProvidersAction,
    toggleProvider as toggleProviderAction,
    untargetProviders as untargetProvidersAction,
    validateRouteDraft,
} from './state';
import type {
    LabRouteEditorAction,
    LabRouteEditorState,
    RouteDraft,
    RouteDraftField,
    RouteDraftIssues,
    TifKind,
    TifSession,
} from './state';
import { effect } from '@preact/signals-core';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { EmptyArray } from '@thinkalpha/common/util/empty.js';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import { t } from 'i18next';
import { generateFirmNameToDisplay } from 'src/admin/firms/utils';
import type { Provider } from 'src/admin/LAB/contracts/providers';
import type { Route } from 'src/admin/LAB/contracts/routes';
import { log } from 'src/admin/LAB/log';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getFirmsQuery } from 'src/queries/firms';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import { getAllRoutesQuery, invalidateRoutesQuery, updateRoute } from 'src/queries/labRoutes';
import type { ReactBindings } from 'src/types/bindings';

type Firms = FirmWithParentsAndChildren[];

const compareRows = (a: ProviderRow, b: ProviderRow): number => {
    if (a.targeted !== b.targeted) return a.targeted ? -1 : 1;
    if (a.targeted) {
        const priorityDiff = (a.bidPriority ?? Number.MAX_SAFE_INTEGER) - (b.bidPriority ?? Number.MAX_SAFE_INTEGER);
        if (priorityDiff !== 0) return priorityDiff;
    }
    return a.name.localeCompare(b.name);
};

const matchesSearch = (row: ProviderRow, search: string): boolean =>
    [row.id, row.name, row.firmName].some((text) => text.toLowerCase().includes(search));

@injectable()
export class LabRouteEditorModelImpl implements LabRouteEditorModel {
    readonly #disposableStack = new DisposableStack();
    readonly #state = signal<LabRouteEditorState>(initialLabRouteEditorState);
    /** Flips once `setScope` runs so no query fires before the route is known. */
    readonly #scopeSet = signal(false);
    /** Set on disposal so the async save continuation bails out. */
    readonly #abort = new AbortController();
    readonly #queryClient: ReactBindings['QueryClient'];
    readonly #store: ReactBindings['Store'];
    readonly #currentUser: ReactBindings['CurrentUserModel'];

    readonly #superUserMode: ReadonlySignal<boolean>;
    readonly #routesQuery: QueryObserverModel<Route[], Error, Route[]>;
    readonly #providersQuery: QueryObserverModel<Provider[], Error, Provider[]>;
    readonly #firmsQuery: QueryObserverModel<Firms, Error, Firms>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('Store') store: ReactBindings['Store'],
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;
        this.#currentUser = currentUser;
        this.#superUserMode = computed(
            () => this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value,
        );
        // Mirrors `useAllRoutes` / `useAllProviders`: a firm scope or superuser mode is required.
        const authorized = computed(
            () => this.#scopeSet.value && (this.#superUserMode.value || Boolean(this.#state.value.firmId)),
        );

        const routesOptions = computed<QueryObserverOptions<Route[], Error, Route[]>>(() => ({
            ...getAllRoutesQuery(this.#state.value.firmId),
            enabled: authorized.value,
        }));
        this.#routesQuery = queryObserverModelFactory<Route[], Error, Route[]>(routesOptions);
        this.#disposableStack.use(this.#routesQuery);

        // Superusers fetch providers unscoped so targeted providers owned by other firms still
        // resolve; the rows are filtered to the route's firm plus targeted outsiders anyway.
        const providersOptions = computed<QueryObserverOptions<Provider[], Error, Provider[]>>(() => ({
            ...getAllProvidersQuery(this.#superUserMode.value ? undefined : this.#state.value.firmId),
            enabled: authorized.value,
        }));
        this.#providersQuery = queryObserverModelFactory<Provider[], Error, Provider[]>(providersOptions);
        this.#disposableStack.use(this.#providersQuery);

        const firmsOptions = computed<QueryObserverOptions<Firms, Error, Firms>>(() => ({
            ...getFirmsQuery(),
            enabled: this.#scopeSet.value,
        }));
        this.#firmsQuery = queryObserverModelFactory<Firms, Error, Firms>(firmsOptions);
        this.#disposableStack.use(this.#firmsQuery);

        // Reseed only when the server copy itself changes, never on our own state writes.
        let lastSyncedRoute: Route | undefined;
        this.#disposableStack.defer(
            effect(() => {
                const route = this.route.value;
                if (!route || route === lastSyncedRoute) return;
                lastSyncedRoute = route;
                this.#dispatch(syncRouteAction(draftFromRoute(route)));
            }),
        );
    }

    #dispatch(action: LabRouteEditorAction): void {
        this.#state.value = labRouteEditorReducer(this.#state.value, action);
    }

    readonly route: ReadonlySignal<Route | undefined> = computed(() => {
        const routeId = this.#state.value.routeId;
        return this.#routesQuery.signal.value.data?.find(({ id }) => id === routeId);
    });
    readonly isLoading: ReadonlySignal<boolean> = computed(() => this.#routesQuery.signal.value.isLoading);
    readonly loadError: ReadonlySignal<boolean> = computed(() => this.#routesQuery.signal.value.isError);
    readonly providersLoading: ReadonlySignal<boolean> = computed(
        () => this.#providersQuery.signal.value.isLoading || this.#firmsQuery.signal.value.isLoading,
    );
    readonly providersError: ReadonlySignal<boolean> = computed(() => this.#providersQuery.signal.value.isError);
    readonly canManage: ReadonlySignal<boolean> = computed(() => this.#state.value.canManage);
    readonly canEdit: ReadonlySignal<boolean> = computed(
        () => this.#state.value.canManage && !this.#state.value.isSaving,
    );

    readonly draft: ReadonlySignal<RouteDraft | null> = computed(() => this.#state.value.draft);
    readonly issues: ReadonlySignal<RouteDraftIssues> = computed(() => {
        const draft = this.#state.value.draft;
        return draft ? validateRouteDraft(draft) : NO_ISSUES;
    });
    readonly isDirty: ReadonlySignal<boolean> = computed(() => {
        const { draft, baseline } = this.#state.value;
        return draft !== null && baseline !== null && !draftsEqual(draft, baseline);
    });
    readonly isSaving: ReadonlySignal<boolean> = computed(() => this.#state.value.isSaving);
    readonly canSave: ReadonlySignal<boolean> = computed(
        () =>
            this.#state.value.canManage &&
            this.isDirty.value &&
            !this.#state.value.isSaving &&
            !hasIssues(this.issues.value),
    );

    readonly providerSearch: ReadonlySignal<string> = computed(() => this.#state.value.providerSearch);

    /** Ids of the route firm's own providers; the "Target all" / "Clear all" universe. */
    readonly #ownProviderIds: ReadonlySignal<readonly string[]> = computed(() => {
        const route = this.route.value;
        const providers = this.#providersQuery.signal.value.data;
        if (!route || !providers) return EmptyArray;
        return providers.filter((p) => p.firmId === route.firmId).map(({ id }) => id);
    });

    readonly #allRows: ReadonlySignal<readonly ProviderRow[]> = computed(() => {
        const route = this.route.value;
        const draft = this.#state.value.draft;
        const providers = this.#providersQuery.signal.value.data;
        if (!route || !draft || !providers) return EmptyArray;
        const firms = this.#firmsQuery.signal.value.data ?? EmptyArray;
        const firmById = new Map(firms.map((firm) => [firm.id, firm] as const));
        const providerById = new Map(providers.map((provider) => [provider.id, provider] as const));
        const targetById = new Map(draft.providers.map((target) => [target.providerId, target] as const));

        const own: ProviderRow[] = providers
            .filter((provider) => provider.firmId === route.firmId)
            .map((provider) => {
                const target = targetById.get(provider.id);
                const firm = firmById.get(provider.firmId);
                return {
                    id: provider.id,
                    name: provider.name,
                    firmName: firm ? generateFirmNameToDisplay(firm) : provider.firmId,
                    targeted: target !== undefined,
                    bidPriority: target?.bidPriority ?? null,
                    isForeign: false,
                    isInvalid: false,
                };
            });
        // Targets outside the route's firm are listed too so they can be seen and removed.
        const outsiders: ProviderRow[] = draft.providers
            .filter(({ providerId }) => providerById.get(providerId)?.firmId !== route.firmId)
            .map(({ providerId, bidPriority }) => {
                const provider = providerById.get(providerId);
                if (!provider) {
                    return {
                        id: providerId,
                        name: t('admin::lab::route-configuration::invalid-provider', 'Invalid provider'),
                        firmName: providerId,
                        targeted: true,
                        bidPriority,
                        isForeign: false,
                        isInvalid: true,
                    };
                }
                const firm = firmById.get(provider.firmId);
                return {
                    id: provider.id,
                    name: provider.name,
                    firmName: firm ? generateFirmNameToDisplay(firm) : provider.firmId,
                    targeted: true,
                    bidPriority,
                    isForeign: true,
                    isInvalid: false,
                };
            });
        return [...own, ...outsiders].sort(compareRows);
    });

    readonly providerRows: ReadonlySignal<readonly ProviderRow[]> = computed(() => {
        const search = this.#state.value.providerSearch.trim().toLowerCase();
        const rows = this.#allRows.value;
        return search ? rows.filter((row) => matchesSearch(row, search)) : rows;
    });
    readonly targetedCount: ReadonlySignal<number> = computed(() => this.#state.value.draft?.providers.length ?? 0);
    readonly allTargeted: ReadonlySignal<boolean> = computed(() => {
        const own = this.#ownProviderIds.value;
        const targets = this.#state.value.draft?.providers ?? EmptyArray;
        return own.length > 0 && own.every((id) => targets.some(({ providerId }) => providerId === id));
    });
    readonly noneTargeted: ReadonlySignal<boolean> = computed(() => {
        const own = this.#ownProviderIds.value;
        const targets = this.#state.value.draft?.providers ?? EmptyArray;
        return !own.some((id) => targets.some(({ providerId }) => providerId === id));
    });

    setScope(firmId: string | undefined, routeId: string): void {
        const state = this.#state.value;
        if (this.#scopeSet.value && (state.firmId !== firmId || state.routeId !== routeId)) {
            throw new Error(
                'LabRouteEditorModel scope is fixed after initialization; create a new instance for a different route',
            );
        }
        this.#dispatch(setScopeAction(firmId, routeId));
        this.#scopeSet.value = true;
    }

    setCanManage(canManage: boolean): void {
        this.#dispatch(setCanManageAction(canManage));
    }

    setField<K extends RouteDraftField>(field: K, value: RouteDraft[K]): void {
        this.#dispatch(setFieldAction(field, value));
    }

    setTifKind(kind: TifKind): void {
        this.#dispatch(setFieldAction('tifKind', kind));
    }

    setTifSession(session: TifSession | null): void {
        this.#dispatch(setFieldAction('tifSession', session));
    }

    toggleProvider(providerId: string): void {
        this.#dispatch(toggleProviderAction(providerId));
    }

    setBidPriority(providerId: string, bidPriority: number | null): void {
        this.#dispatch(setBidPriorityAction(providerId, bidPriority));
    }

    targetAll(): void {
        this.#dispatch(targetProvidersAction([...this.#ownProviderIds.value]));
    }

    clearAll(): void {
        this.#dispatch(untargetProvidersAction([...this.#ownProviderIds.value]));
    }

    setProviderSearch(value: string): void {
        this.#dispatch(setProviderSearchAction(value));
    }

    discard(): void {
        this.#dispatch(discardAction());
    }

    async save(): Promise<void> {
        const route = this.route.value;
        const draft = this.#state.value.draft;
        if (!route || !draft || !this.canSave.value) return;
        this.#dispatch(startSaveAction());
        try {
            const saved = await updateRoute({ id: route.id, ...draftToUpdateRequest(draft) });
            // Disposed mid-save (route switch): the write landed, so the shared routes cache is
            // still refreshed below, but nothing may land on the dead instance.
            if (!this.#abort.signal.aborted) {
                this.#dispatch(finishSaveAction(draftFromRoute(saved)));
                this.#store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(
                            'admin::lab::route-configuration::update-success',
                            'Updated configuration for {{name}}',
                            { name: saved.name },
                        ),
                        group: 'default',
                        variant: 'success',
                    },
                });
            }
            await invalidateRoutesQuery(this.#queryClient);
        } catch (error) {
            log.error(`Saving LAB route ${route.id} (${route.name}) failed`, { error, routeId: route.id });
            if (this.#abort.signal.aborted) return;
            this.#dispatch(failSaveAction());
            this.#store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t(
                        'admin::lab::route-configuration::update-error',
                        'Error updating configuration for {{name}}',
                        {
                            name: route.name,
                        },
                    ),
                    group: 'default',
                    variant: 'error',
                },
            });
        }
    }

    [Symbol.dispose](): void {
        this.#abort.abort();
        this.#disposableStack.dispose();
    }
}
